import { TokenInfo, DecodedJWT } from "@/core/auth/types";
import { SecureStorage } from "@/core/secure-storage";
import { jwtDecode } from 'jwt-decode';
import { appConfig } from '@/core/config';
import { messageHandler } from '@/core/auth/message-handler';

export class TokenManager {
  private static readonly APP_TOKEN_KEY = 'X-App-Token';
  private static readonly ACCOUNT_TOKEN_KEY = 'X-Account-Token';
  private static readonly ACCOUNT_ID_KEY = 'X-Account-Id';
  private static readonly OPERATOR_ID_KEY = 'X-Operator-Id';
  private static readonly TOKEN_EXPIRY_KEY = 'token_expiry';
  private static readonly AUTHORIZATION_HEADER = 'authorization';
  private static readonly CORTEX_SESSION_KEY = 'cortex_session'; // External token key (used in both session and local storage)

  // Flag to prevent restoring cleared tokens (prevents infinite loops)
  private tokensCleared: boolean = false;

  constructor(
    private readonly storage: SecureStorage,
    private readonly jwtDecoder: typeof jwtDecode
  ) {}

  async saveTokens(
    appToken: string,
    accountToken: string,
    accountId: string,
    operatorId?: string,
    distributorId?: string
  ): Promise<void> {
    const decoded = this.jwtDecoder<DecodedJWT>(appToken);
    const expiry = new Date(decoded.exp * 1000);

    const saveOperations = [
      this.storage.setItem(TokenManager.APP_TOKEN_KEY, appToken),
      this.storage.setItem(TokenManager.ACCOUNT_TOKEN_KEY, accountToken),
      this.storage.setItem(TokenManager.ACCOUNT_ID_KEY, accountId),
      this.storage.setItem(TokenManager.TOKEN_EXPIRY_KEY, expiry.toISOString())
    ];

    // Save operatorId only if provided
    if (operatorId) {
      saveOperations.push(this.storage.setItem(TokenManager.OPERATOR_ID_KEY, operatorId));
    }

    // Save distributorId only if provided
    if (distributorId) {
      saveOperations.push(this.storage.setItem('X-Distributor-Id', distributorId));
    }

    await Promise.all(saveOperations);
  }

  async getTokens(): Promise<TokenInfo | null> {
    const [appToken, accountToken, accountId, operatorId, distributorId, expiryStr] = await Promise.all([
      this.storage.getItem(TokenManager.APP_TOKEN_KEY),
      this.storage.getItem(TokenManager.ACCOUNT_TOKEN_KEY),
      this.storage.getItem(TokenManager.ACCOUNT_ID_KEY),
      this.storage.getItem(TokenManager.OPERATOR_ID_KEY),
      this.storage.getItem('X-Distributor-Id'), // Also retrieve distributorId
      this.storage.getItem(TokenManager.TOKEN_EXPIRY_KEY)
    ]);

    // Only require essential tokens (appToken, accountToken, accountId)
    // operatorId can be undefined for distributor users or during operator selection
    if (!appToken || !accountToken || !accountId) return null;

    let expiry: Date | null = null;
    if (expiryStr) {
      try {
        const date = new Date(expiryStr);
        if (!isNaN(date.getTime())) {
          expiry = date;
        }
      } catch (error) {
        console.warn('Invalid token expiry date:', expiryStr);
      }
    }

    return {
      appToken,
      accountToken,
      accountId,
      operatorId: operatorId || undefined, // Convert null to undefined
      distributorId: distributorId || undefined, // Include distributorId
      expiry
    };
  }

  async clearTokens(): Promise<void> {
    // Set flag to prevent restoring cleared tokens
    this.tokensCleared = true;
    
    await Promise.all([
      this.storage.removeItem(TokenManager.APP_TOKEN_KEY),
      this.storage.removeItem(TokenManager.ACCOUNT_TOKEN_KEY),
      this.storage.removeItem(TokenManager.ACCOUNT_ID_KEY),
      this.storage.removeItem(TokenManager.OPERATOR_ID_KEY),
      this.storage.removeItem(TokenManager.TOKEN_EXPIRY_KEY),
      this.storage.removeItem(TokenManager.CORTEX_SESSION_KEY)
    ]);
    
    // Also clear sessionStorage
    try {
      sessionStorage.removeItem(TokenManager.CORTEX_SESSION_KEY);
    } catch (error) {
      console.warn('Failed to clear cortex_session from sessionStorage:', error);
    }
    
  }

  async getAuthHeaders(): Promise<Record<string, string>> {
    // PRIORITY 1: Check cortex_session first (external token)
    const cortexSession = await this.getCortexSession();
    if (cortexSession) {
      try {
        const decoded = this.jwtDecoder<DecodedJWT>(cortexSession);
        const jwtOperatorId = (decoded.OperatorId || decoded.operatorId || decoded.actor)?.toString();
        const jwtAccountId = (decoded.AccountId || decoded.accountId)?.toString();

        return {
          [TokenManager.AUTHORIZATION_HEADER]: `Bearer ${cortexSession}`,
          [TokenManager.OPERATOR_ID_KEY]: jwtOperatorId || 'unknown',
          [TokenManager.ACCOUNT_ID_KEY]: jwtAccountId || 'unknown'
        };
      } catch (error) {
        console.warn('⚠️ AUTH HEADERS: Failed to decode cortex_session, falling back to internal tokens', error);
      }
    }

    // PRIORITY 2: Use internal tokens (existing system)
    const tokens = await this.getTokens();
    if (!tokens) {
      return {};
    }

    // Use operator ID directly from token (can be undefined for distributors)
    const operatorId = tokens.operatorId;
    const distributorId = tokens.distributorId;

    const headers: Record<string, string> = {
      [TokenManager.AUTHORIZATION_HEADER]: `Bearer ${tokens.appToken}`,
      [TokenManager.ACCOUNT_ID_KEY]: tokens.accountId
    };

    // Add operatorId header only if available
    if (operatorId) {
      headers[TokenManager.OPERATOR_ID_KEY] = operatorId;
    }

    // Add distributorId header only if available
    if (distributorId) {
      headers['X-Distributor-Id'] = distributorId;
    }

    return headers;
  }

  async isTokenValid(): Promise<boolean> {
    const tokens = await this.getTokens();
    if (!tokens || !tokens.expiry) return false;

    // Add 5 minutes buffer to prevent edge cases
    const now = new Date();
    const bufferTime = 5 * 60 * 1000; // 5 minutes in milliseconds
    return tokens.expiry.getTime() > now.getTime() + bufferTime;
  }

  /**
   * Get cortex_session token (SINGLE SOURCE OF TRUTH)
   * Priority 1: sessionStorage (current session)
   * Priority 2: localStorage (persisted across sessions) - validates before restoring
   */
  async getCortexSession(): Promise<string | null> {
    // PRIORITY 1: Check sessionStorage first
    try {
      const sessionToken = sessionStorage.getItem(TokenManager.CORTEX_SESSION_KEY);
      if (sessionToken) {
        return sessionToken;
      }
    } catch (error) {
      console.warn('Failed to get cortex_session from sessionStorage:', error);
    }

    // PRIORITY 2: Check localStorage for persistence
    if (this.tokensCleared) {
      return null;
    }

    try {
      const persistedToken = await this.storage.getItem(TokenManager.CORTEX_SESSION_KEY);
      
      if (persistedToken) {
        try {
          const decoded = this.jwtDecoder<DecodedJWT>(persistedToken);
          const now = Date.now() / 1000;
          
          // Check if token is expired (with 1-minute buffer)
          if (decoded.exp < now + 60) {
            console.warn('⚠️ Token in localStorage expired - clearing');
            await this.storage.removeItem(TokenManager.CORTEX_SESSION_KEY);
            return null;
          }
          
          sessionStorage.setItem(TokenManager.CORTEX_SESSION_KEY, persistedToken);
          return persistedToken;
          
        } catch (decodeError) {
          console.warn('⚠️ Failed to decode persisted token - clearing:', decodeError);
          await this.storage.removeItem(TokenManager.CORTEX_SESSION_KEY);
          return null;
        }
      }
    } catch (error) {
      console.warn('Failed to get cortex_session from localStorage:', error);
    }

    return null;
  }

  /**
   * Save cortex_session to localStorage (persists across sessions)
   */
  async saveCortexSession(token: string): Promise<void> {
    try {
      await this.storage.setItem(TokenManager.CORTEX_SESSION_KEY, token);
      // Reset cleared flag - we have a new valid token
      this.tokensCleared = false;
    } catch (error) {
      console.error('Failed to save cortex_session to localStorage:', error);
    }
  }

  /**
   * Checks if cortex_session token is valid
   * Uses 1-minute buffer to avoid rejecting tokens backend would accept
   * Optionally requests fresh token if near expiration
   */
  async isCortexSessionValid(requestRefreshIfNearExpiration = false): Promise<boolean> {
    try {
      const cortexSession = await this.getCortexSession();
      if (!cortexSession) return false;

      const decoded = this.jwtDecoder<DecodedJWT>(cortexSession);
      const now = Date.now() / 1000;
      const bufferTime = 60; // 1 minute buffer (reduced from 5 minutes)
      const refreshThreshold = 5 * 60; // 5 minutes - request refresh if token expires within this time
      
      const timeUntilExpiration = decoded.exp - now;
      
      // If token is expired or will expire soon (within buffer), it's invalid
      if (timeUntilExpiration <= bufferTime) {
        console.warn('Token expired or expiring within buffer');
        return false;
      }
      
      // If token is near expiration (< 5 minutes) and refresh requested, request fresh token
      if (requestRefreshIfNearExpiration && timeUntilExpiration < refreshThreshold) {
        messageHandler.requestFreshToken();
      }
      
      return true;
    } catch (error) {
      console.warn('Failed to validate cortex_session:', error);
      return false;
    }
  }

} 